import { getEnv } from '../configs/env';

// Pure API client without React hooks
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiRequestOptions {
  method?: HttpMethod;
  params?: Record<string, string | number>;
  body?: any;
  extraHeaders?: HeadersInit;
  token?: string;
}

// User Eligibility API
export interface UserEligibilityResponse {
  isEligible: boolean;
  playerNeedsOptIn: boolean;
  isPromotionRunning: boolean;
}

// Balance API
export interface RawBalanceItem {
  id?: number;
  amount: number;
  coin?: string;
  coinId?: string;
  coinType?: number;
  coinUrl?: string;
}

// Aggregation Progress API (graph endpoint)
export interface AggregationProgressLinear {
  every: string;
  give: string;
  limit: string | null;
}

export interface AggregationProgressAwardRule {
  mode: string;
  memVar: string;
  linear: AggregationProgressLinear | null;
  steps: unknown | null;
}

export interface AggregationProgressResponse {
  awardRule: AggregationProgressAwardRule;
  currentValue: number;
  awardedSoFar: number;
  anchorMemVar: number;
  anchorAwarded: number;
  memory: Record<string, number>;
  awardedLastReset: number | null;
  inputOverrides: unknown[];
  activation: unknown | null;
  resolvedInputs: Record<string, unknown>;
}

export let Token = '';
export let Ott = '';

export async function apiRequest<T = any>(
  endpoint: string,
  {
    method = "GET",
    params,
    body,
    extraHeaders = {},
  }: ApiRequestOptions = {}
): Promise<T> {
  let url = endpoint;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    url += `?${searchParams.toString()}`;
  }
  const headers = new Headers(extraHeaders);
  headers.set("Authorization", `Bearer ${Token}`);
  if (body && method !== "GET") {
    headers.set("Content-Type", "application/json");
  }

  const init: RequestInit = { method, headers };
  if (body && method !== "GET") {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(
      `Fetch error ${response.status}: ${response.statusText}`
    );
  }

  const data = await response.json();
  return data as T;
}

// Widget API
export interface IWidgetConfig {
  promotionId: number;
  coinId: string;
  coinUrl: string;
  triggerId: string;
  pageUrl: string;
}

export interface WidgetData {
  id: string;
  data: string;
}

export async function fetchWidget(widgetId: string): Promise<WidgetData> {
  return apiRequest<WidgetData>(`${getEnv().WIDGET_SERVICE_BASE_URL}/api/widgets/${widgetId}`, {
    method: 'GET',
  });
}

export async function fetchPlayerBalances(coinId: string): Promise<RawBalanceItem> {
  const response = await apiRequest<RawBalanceItem>(`${getEnv().GATEWAY_BASE_URL}/hubapi/Hub/GetPlayerBalancesByCoinId`, {
    method: 'GET',
    params: { coinId },
  });
  return response || { amount: 0 };
}



export async function checkUserEligibility(
  promotionId: number,
): Promise<UserEligibilityResponse> {
  return apiRequest<UserEligibilityResponse>(
    `${getEnv().GATEWAY_BASE_URL}/hubapi/Hub/CheckUserEligibilityV2`,
    {
      method: "GET",
      params: { PromotionId: promotionId },
    }
  );
}

// Authentication API
export async function authenticateWithOtp(
  casinoToken: string,
  promotionId: number
): Promise<string | boolean> {
  try {
    const body: Record<string, unknown> = {
      casinoToken,
      promotionId,
    };
    const response = await apiRequest<{ accessToken: string; refreshToken: string }>(
      `${getEnv().GATEWAY_BASE_URL}/playersegmentsapi/api/Player/Authentificate`,
      {
        method: "POST",
        body,
      }
    );

    Token = response.accessToken;
    Ott = casinoToken;
    return response.accessToken;
  } catch (error) {
    console.error("OTP authentication error:", error);
    return false;
  }
}

// Aggregation Progress API
export async function fetchAggregationProgress(
  configurationId: string
): Promise<AggregationProgressResponse> {
  return apiRequest<AggregationProgressResponse>(
    `${getEnv().GATEWAY_BASE_URL}/aggregationapi/api/aggregation-graph/${configurationId}/progress`,
    {
      method: "GET",
    }
  );
}
